import {
    Filters,
    FilterFactoryCreator,
    FilterFactory,
    argsExcept,
    getFilterIds,
    PageKeys
} from "@shared";
import { LoaderArgumentsMap } from "../load-manager";
import { Observable } from "rxjs";
import { VariationFiltersProvider } from "./variation-filter-provider.service";
import { LgFilterSet } from "@logex/framework/lg-filterset";
import { Injectable } from "@angular/core";

@Injectable()
export class ProcessVariationFilterProvider extends VariationFiltersProvider {
    public get pagekey(): string {
        return PageKeys.PROCESS_VAR_PAGE_KEY;
    }

    _getLocalizedFilters() {
        return {
            ...super._getLocalizedFilters(),
            [Filters.AC1]: {
                name: "APP._Filters.Ac1",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("ac1", value);
                }
            },
            [Filters.AC2]: {
                name: "APP._Filters.Ac2",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("ac2", value);
                }
            },
            [Filters.AC3]: {
                name: "APP._Filters.Ac3",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("ac3", value);
                }
            },
            [Filters.ACTIVITY]: {
                name: "FW._Filters.Activity",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("activity", value);
                }
            },
            [Filters.IS_COMPLEX]: {
                name: "APP._Filters.IsComplex",
                options: (value: number | string) => {
                    return +value === 0
                        ? "APP._Filters.IsComplex__no"
                        : "APP._Filters.IsComplex__yes";
                }
            },
            [Filters.MULTIDISCIPLINARY_SPECIALISM]: {
                name: "APP._Filters.MultidisciplinarySpecialism",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("specialism", value);
                }
            },
            [Filters.SPECIALISM_DIFF_COST]: {
                name: "APP._Filters.SpecialismDiffCost",
                options: (value: { from: number; to: number }) => {
                    return ` from ${value.from || "-∞"} to ${value.to || "∞"}`;
                }
            }
        };
    }

    protected _configureFilters(
        filterFactory: FilterFactory,
        source: (filterName: string) => Observable<any>
    ) {
        return super._configureFilters(filterFactory, source);
    }

    protected _definePatientTypes(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    ) {
        super._definePatientTypes(filterFactoryCreator, source);
        filterFactoryCreator.startGroup("APP._FilterGroups.PatientType");
        filterFactoryCreator.isUrgencyCareFilter({
            source: () => source(Filters.URGENCY_CARE)
        });
    }

    protected _defineSpecificFiltersGroups(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    ): void {
        filterFactoryCreator
            .startGroup("APP._FilterGroups.Activity")
            .ac1Filter({ source: () => source(Filters.AC1) })
            .ac2Filter({ source: () => source(Filters.AC2) })
            .ac3Filter({ source: () => source(Filters.AC3) })
            .activityFilter({ source: () => source(Filters.ACTIVITY) });
    }

    protected _defineTreatmentChoices(
        filterFactoryCreator: FilterFactoryCreator<any, any>,
        source: (filterName: string) => Observable<any>
    ) {
        super._defineTreatmentChoices(filterFactoryCreator, source);
        filterFactoryCreator.isComplexFilter({
            source: () => source(Filters.IS_COMPLEX),
            visible: () => true
        });
    }

    protected _defineCareQuestionsGroup(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    ) {
        super._defineCareQuestionsGroup(filterFactoryCreator, source);
        filterFactoryCreator.multidisciplinarySpecialismFilter({
            source: () => source(Filters.SPECIALISM),
            visible: () => true
        });
    }

    protected _defineSpecialism(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    ) {
        filterFactoryCreator.startGroup().specialismFilter({
            source: () => source(Filters.SPECIALISM)
        });
    }

    protected _defineCostDifferenceFilters(filterFactoryCreator: FilterFactoryCreator<any>) {
        super._defineCostDifferenceFilters(filterFactoryCreator);
        filterFactoryCreator.rangeMoneyFilter(Filters.SPECIALISM_DIFF_COST, {
            nameLC: "APP._Filters.SpecialismDiffCost"
        });
    }

    _getLoaderArguments(filterContext: LgFilterSet<any, any>): LoaderArgumentsMap {
        return {
            ...super._getLoaderArguments(filterContext),
            [Filters.ACTIVITY]: () => getFilterIds(Filters.ACTIVITY, filterContext),
            [Filters.AC1]: () => getFilterIds(Filters.AC1, filterContext),
            [Filters.AC2]: () => getFilterIds(Filters.AC2, filterContext),
            [Filters.AC3]: () => getFilterIds(Filters.AC3, filterContext),
            [Filters.URGENCY_CARE]: () => getFilterIds(Filters.URGENCY_CARE, filterContext),
            [Filters.MULTIDISCIPLINARY_SPECIALISM]: () =>
                getFilterIds(Filters.MULTIDISCIPLINARY_SPECIALISM, filterContext),
            [Filters.IS_COMPLEX]: () => getFilterIds(Filters.IS_COMPLEX, filterContext)
        } as LoaderArgumentsMap;
    }

    protected _applySource(source: any, filterName: string) {
        return source(filterName, {
            [Filters.AC1]: () => undefined,
            [Filters.AC2]: () => undefined,
            [Filters.AC3]: () => undefined,
            [Filters.ACTIVITY]: () => undefined
        });
    }

    _createLoaders(
        args: LoaderArgumentsMap,
        action: (filterName: string, args: LoaderArgumentsMap) => any
    ): any {
        return {
            ...super._createLoaders(args, action),
            ...action(Filters.ACTIVITY, argsExcept(args, Filters.ACTIVITY)),
            ...action(Filters.AC1, argsExcept(args, Filters.AC1)),
            ...action(Filters.AC2, argsExcept(args, Filters.AC2)),
            ...action(Filters.AC3, argsExcept(args, Filters.AC3)),
            ...action(Filters.IS_COMPLEX, argsExcept(args, Filters.IS_COMPLEX)),
            ...action(Filters.URGENCY_CARE, argsExcept(args, Filters.URGENCY_CARE)),
            ...action(
                Filters.MULTIDISCIPLINARY_SPECIALISM,
                argsExcept(args, Filters.MULTIDISCIPLINARY_SPECIALISM)
            )
        };
    }
}
